import { MeasurementMarkerDto, PlayerOptionDto, SessionKindGroupName, SessionOptionDto, SessionPreviewPointDto, CornerSide } from 'types/models/SessionsPageData';
import { measurementResultBucket, isBinaryOnTargetKind } from 'utilities/chartUtils';
import type { OnTargetTrendPoint } from 'pages/PlayerPerformance/OnTargetTrend';
import type { DailyStatsEntry } from 'types/models/OrgSnapshotData';

export interface RecentPlayer {
  id: string;
  name: string;
  avatarUrl?: string | null;
  teamName: string;
  clubName: string;
  sessionCount: number;
  /** All of the player's kicks / throws (measurements) across every session kind. */
  kickCount: number;
  /** Sessions / kicks per session kind, from the roster; absent or empty when the snapshot has no split. */
}

export interface ClubTeamCell {
  key: string;
  clubName: string;
  teamName: string;
}

export interface SessionCardItem {
  sessionId: string;
  playerId: string;
  playerName: string;
  avatarUrl?: string | null;
  kind: SessionKindGroupName;
  kickCount: number;
  capturedAtUtc: string;
  /** Kicks the card preview is drawn from, when the landing snapshot carries them. */
  previewPoints?: SessionPreviewPointDto[];
}

export interface SessionTypeTabItem {
  kind: SessionKindGroupName | null;
  filterValue: string;
  labelKey: string;
  count: number;
}

export interface PlayerDisciplineSummary {
  kind: SessionKindGroupName;
  filterValue: string;
  labelKey: string;
  kickCount: number;
  sessionCount: number;
  measurements: MeasurementMarkerDto[];
  trendPoints: OnTargetTrendPoint[];
}

export interface PlayerGeneralSummary {
  sessionCount: number;
  measurementCount: number;
  lastSessionDate: string;
  lastSessionMeasurementCount: number;
  lastSessionKindLabelKey: string;
}

export const GENERAL_SESSION_TYPE = 'General';

/**
 * Whole hours between `fromIso` and `nowMs` (defaults to now), rounded to the nearest hour and
 * floored at 0. Backs the snapshot "generated N hours ago" label — intentionally always expressed
 * in hours (never days) so the age stays directly comparable across snapshots.
 */
export function hoursSince(fromIso: string, nowMs: number = Date.now()): number {
  const from = new Date(fromIso).getTime();
  if (Number.isNaN(from)) return 0;
  return Math.max(0, Math.round((nowMs - from) / 3_600_000));
}

/** Deterministic avatar/chip palette (not theme colors — intentional constants). */
const TEAM_PALETTE = ['#2563EB', '#059669', '#D97706', '#DC2626', '#7C3AED', '#0891B2', '#C2410C', '#DB2777'];

/** Maps a session kind group to the Players-page session-type filter value + label key. */
export const KIND_FILTER: Record<SessionKindGroupName, { filterValue: string; labelKey: string }> = {
  FreeKick:   { filterValue: 'FreeKicks',              labelKey: 'sessions.kindFreeKick' },
  Penalty:    { filterValue: 'Penalties',              labelKey: 'sessions.kindPenalty' },
  Corner:     { filterValue: 'CornerKicks',            labelKey: 'sessions.kindCorner' },
  Shooting:   { filterValue: 'ShootingPractice',       labelKey: 'sessions.kindShooting' },
  Goalkeeper: { filterValue: 'GoalkeeperDistribution', labelKey: 'sessions.kindGoalkeeper' },
  ThrowIn:    { filterValue: 'ThrowIn',                labelKey: 'sessions.kindThrowIn' },
};

const KIND_ORDER: SessionKindGroupName[] = ['FreeKick', 'Penalty', 'Corner', 'Shooting', 'Goalkeeper', 'ThrowIn'];

export type TrendGroupBy = 'sessions' | 'weeks' | 'months' | 'quarters' | 'years';

export const TREND_GROUP_OPTIONS: ReadonlyArray<{ key: TrendGroupBy; labelKey: string }> = [
  { key: 'sessions', labelKey: 'playerPerformance.groupBySessions' },
  { key: 'weeks',    labelKey: 'playerPerformance.groupByWeeks' },
  { key: 'months',   labelKey: 'playerPerformance.groupByMonths' },
  { key: 'quarters', labelKey: 'playerPerformance.groupByQuarters' },
  { key: 'years',    labelKey: 'playerPerformance.groupByYears' },
];

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0].toUpperCase()).join('');
}

export function teamColor(teamName: string): string {
  let hash = 0;
  for (let i = 0; i < teamName.length; i += 1) {
    hash = (hash * 31 + teamName.charCodeAt(i)) >>> 0;
  }
  return TEAM_PALETTE[hash % TEAM_PALETTE.length];
}

export function teamKey(clubName: string, teamName: string): string {
  return `${clubName}␟${teamName}`;
}

function sessionCountsByPlayer(measurements: MeasurementMarkerDto[]): Map<string, number> {
  const byPlayer = new Map<string, Set<string>>();
  for (const m of measurements) {
    const set = byPlayer.get(m.playerId) ?? new Set<string>();
    set.add(m.sessionId);
    byPlayer.set(m.playerId, set);
  }
  const counts = new Map<string, number>();
  byPlayer.forEach((sessions, playerId) => {
    counts.set(playerId, sessions.size);
  });
  return counts;
}

/**
 * Session count for a player: measurement-derived when available, otherwise the roster's own
 * `sessionCount`. The landing page carries no measurements, so it falls back to the roster figure
 * (from the org-basic snapshot); the loaded player page uses the precise measurement-derived count.
 */
function effectiveSessionCount(p: PlayerOptionDto, counts: Map<string, number>): number {
  return counts.get(p.id) ?? p.sessionCount ?? 0;
}

/** Kick count for a player: the loaded measurements when the player is open (same source as the session count), else the roster's. */
function kickCountsByPlayer(measurements: MeasurementMarkerDto[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const m of measurements) counts.set(m.playerId, (counts.get(m.playerId) ?? 0) + 1);
  return counts;
}

function toRecentPlayers(players: PlayerOptionDto[], measurements: MeasurementMarkerDto[]): RecentPlayer[] {
  const counts = sessionCountsByPlayer(measurements);
  const kicks = kickCountsByPlayer(measurements);
  return players
    .map((p) => ({
      id: p.id,
      name: p.name,
      avatarUrl: p.avatarUrl,
      teamName: p.teamName,
      clubName: p.clubName,
      sessionCount: effectiveSessionCount(p, counts),
      kickCount: kicks.get(p.id) ?? p.kickCount ?? 0,
    }))
    .filter((p) => p.sessionCount >= 1)
    .sort((a, b) => b.sessionCount - a.sessionCount);
}

export function recentPlayers(players: PlayerOptionDto[], measurements: MeasurementMarkerDto[]): RecentPlayer[] {
  return toRecentPlayers(players, measurements);
}

export function clubTeamCells(players: PlayerOptionDto[], measurements: MeasurementMarkerDto[]): ClubTeamCell[] {
  const counts = sessionCountsByPlayer(measurements);
  const activeTeamKeys = new Set<string>();
  for (const p of players) {
    if (effectiveSessionCount(p, counts) >= 1) activeTeamKeys.add(teamKey(p.clubName, p.teamName));
  }
  const byKey = new Map<string, ClubTeamCell>();
  for (const p of players) {
    const key = teamKey(p.clubName, p.teamName);
    if (activeTeamKeys.has(key) && !byKey.has(key)) byKey.set(key, { key, clubName: p.clubName, teamName: p.teamName });
  }
  return Array.from(byKey.values()).sort(
    (a, b) => a.clubName.localeCompare(b.clubName) || a.teamName.localeCompare(b.teamName),
  );
}

export function playersForTeam(
  players: PlayerOptionDto[],
  measurements: MeasurementMarkerDto[],
  key: string,
): RecentPlayer[] {
  const inTeam = players.filter((p) => teamKey(p.clubName, p.teamName) === key);
  return toRecentPlayers(inTeam, measurements);
}

/** Session cards narrowed to the chosen session types (empty = all). */
export function filterSessionCards(cards: SessionCardItem[], kinds: ReadonlyArray<string>): SessionCardItem[] {
  return kinds.length === 0 ? cards : cards.filter((c) => kinds.includes(c.kind));
}

/**
 * Recent-session cards for the landing page, built from the lean org-basic recent-session list
 * (which carries player ids but no measurements) rather than from measurements. Each session expands
 * to one card per participating player, using the roster for names; the session-level kick count is
 * shown per card. Sessions without player ids are skipped.
 */
export function recentSessionCardsFromSessions(
  sessions: SessionOptionDto[],
  players: PlayerOptionDto[],
): SessionCardItem[] {
  const playerById = new Map(players.map((p) => [p.id, p]));
  const cards: SessionCardItem[] = [];
  for (const s of sessions) {
    for (const playerId of s.playerIds ?? []) {
      cards.push({
        sessionId: s.id,
        playerId,
        playerName: playerById.get(playerId)?.name ?? playerId,
        avatarUrl: playerById.get(playerId)?.avatarUrl,
        kind: s.kind,
        kickCount: s.playerKickCounts?.[playerId] ?? s.kickCount,
        capturedAtUtc: s.capturedAtUtc,
        previewPoints: s.playerPreviews?.[playerId],
      });
    }
  }
  return cards.sort((a, b) => b.capturedAtUtc.localeCompare(a.capturedAtUtc));
}

export function playerSessionTypeTabs(measurements: MeasurementMarkerDto[], playerId: string): SessionTypeTabItem[] {
  const summaries = buildPlayerDisciplineSummaries(measurements, playerId);
  if (summaries.length === 0) return [];

  return [
    {
      kind: null,
      filterValue: GENERAL_SESSION_TYPE,
      labelKey: 'playerPerformance.generalTab',
      count: summaries.reduce((sum, summary) => sum + summary.kickCount, 0),
    },
    ...summaries.map((summary) => ({
      kind: summary.kind,
      filterValue: summary.filterValue,
      labelKey: summary.labelKey,
      count: summary.kickCount,
    })),
  ];
}

export function buildPlayerDisciplineSummaries(
  measurements: MeasurementMarkerDto[],
  playerId: string,
): PlayerDisciplineSummary[] {
  return KIND_ORDER
    .map((kind) => {
      const playerKindMeasurements = measurements.filter((m) => m.playerId === playerId && m.kind === kind);
      if (playerKindMeasurements.length === 0) return null;

      return {
        kind,
        filterValue: KIND_FILTER[kind].filterValue,
        labelKey: KIND_FILTER[kind].labelKey,
        kickCount: playerKindMeasurements.length,
        sessionCount: new Set(playerKindMeasurements.map((m) => m.sessionId).filter((id): id is string => Boolean(id))).size,
        measurements: playerKindMeasurements,
        trendPoints: buildOnTargetTrendPoints(playerKindMeasurements, 1),
      } satisfies PlayerDisciplineSummary;
    })
    .filter((summary): summary is PlayerDisciplineSummary => summary != null);
}

function formatSummaryDate(dateStr: string): string {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;

  const day = `${date.getUTCDate()}`.padStart(2, '0');
  const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}.${month}.${year}`;
}

export function buildPlayerGeneralSummary(
  measurements: MeasurementMarkerDto[],
  playerId: string,
): PlayerGeneralSummary | null {
  const playerMeasurements = measurements.filter((measurement) => measurement.playerId === playerId);
  if (playerMeasurements.length === 0) return null;

  const sessions = new Map<string, { latestCapturedAtUtc: string; measurementCount: number; kind: SessionKindGroupName }>();
  for (const measurement of playerMeasurements) {
    const existing = sessions.get(measurement.sessionId);
    if (!existing) {
      sessions.set(measurement.sessionId, {
        latestCapturedAtUtc: measurement.capturedAtUtc,
        measurementCount: 1,
        kind: measurement.kind,
      });
      continue;
    }

    existing.measurementCount += 1;
    if (measurement.capturedAtUtc > existing.latestCapturedAtUtc) {
      existing.latestCapturedAtUtc = measurement.capturedAtUtc;
      existing.kind = measurement.kind;
    }
  }

  const latestSession = Array.from(sessions.values())
    .sort((left, right) => right.latestCapturedAtUtc.localeCompare(left.latestCapturedAtUtc))[0];

  return {
    sessionCount: sessions.size,
    measurementCount: playerMeasurements.length,
    lastSessionDate: formatSummaryDate(latestSession.latestCapturedAtUtc),
    lastSessionMeasurementCount: latestSession.measurementCount,
    lastSessionKindLabelKey: KIND_FILTER[latestSession.kind].labelKey,
  };
}

// ── Date bucketing helpers (shared by OnTargetTrend and ResultBreakdownChart) ──

/**
 * Returns the ISO date string (YYYY-MM-DD) of the Monday that starts the week
 * containing `dateStr`. Computed entirely in UTC — inputs are UTC timestamps, so
 * the result is stable regardless of the running environment's timezone.
 */
export function getMondayOf(dateStr: string): string {
  const d = new Date(dateStr);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  d.setUTCHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

/** Returns the `{ key, label }` for the ISO-week bucket containing `dateStr`. */
export function getWeekKey(dateStr: string): { key: string; label: string } {
  const mondayStr = getMondayOf(dateStr);
  const mon = new Date(mondayStr);
  const sun = new Date(mon);
  sun.setUTCDate(sun.getUTCDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return { key: mondayStr, label: `${fmt(mon)} – ${fmt(sun)}` };
}

// ── Per-session on-target trend ────────────────────────────────────────────────

/**
 * Aggregates measurements into per-session on-target stats.
 * Sessions with fewer than `minKicksPerSession` kicks are excluded (too noisy).
 * Result is sorted by capturedAtUtc ascending.
 */
export function buildOnTargetTrendPoints(
  measurements: MeasurementMarkerDto[],
  minKicksPerSession = 3,
): OnTargetTrendPoint[] {
  const sessionMap = new Map<string, { capturedAtUtc: string; total: number; onTarget: number }>();
  for (const m of measurements) {
    const existing = sessionMap.get(m.sessionId) ?? { capturedAtUtc: m.capturedAtUtc, total: 0, onTarget: 0 };
    existing.total += 1;
    // Kind-aware: continuous success for binary kinds (so the trend %/average matches the zone + tile),
    // 0/1 bucket for discrete kinds. `onTarget` is therefore a fractional sum; onTarget/total is the mean.
    existing.onTarget += onTargetContribution(m);
    if (m.capturedAtUtc > existing.capturedAtUtc) existing.capturedAtUtc = m.capturedAtUtc;
    sessionMap.set(m.sessionId, existing);
  }
  return Array.from(sessionMap.entries())
    .filter(([, s]) => s.total >= minKicksPerSession)
    .map(([sessionId, s]) => ({
      sessionId,
      capturedAtUtc: s.capturedAtUtc,
      total: s.total,
      onTarget: s.onTarget,
      onTargetPct: s.total > 0 ? (s.onTarget / s.total) * 100 : 0,
    }))
    .sort((a, b) => a.capturedAtUtc.localeCompare(b.capturedAtUtc));
}

// ── Activity heatmap data ─────────────────────────────────────────────────────

/** Aggregates measurements into daily activity entries for the heatmap. */
export function buildActivityData(measurements: MeasurementMarkerDto[], teamName: string): DailyStatsEntry[] {
  const byDate = new Map<string, { sessions: Set<string>; count: number }>();
  for (const m of measurements) {
    const date = m.capturedAtUtc.slice(0, 10);
    const entry = byDate.get(date) ?? { sessions: new Set<string>(), count: 0 };
    entry.sessions.add(m.sessionId);
    entry.count += 1;
    byDate.set(date, entry);
  }
  return Array.from(byDate.entries()).map(([date, e]) => ({
    date,
    sessions: e.sessions.size,
    measurements: e.count,
    players: 1,
    teamName,
  }));
}

// ── Summary tile stats (player, for the top + below-view cells) ───────────────

export interface KickTileStats {
  totalKicks: number;
  onTargetPct: number | null;
  avgBallSpeed: number | null;
  avgSpinRate: number | null;
  avgPullDirection: number | null;
  /** Average spin-axis angle (deg) — feeds the pull-direction ball's axis line. Arithmetic mean, same
   *  approximation as avgPullDirection. */
  avgSpinAxis: number | null;
  avgWallCrossingHeight: number | null;
}

/**
 * Aggregates the summary-tile numbers from a set of already-loaded kick markers. Used for both the
 * top per-kind cells and the below-view cells so they always agree (and don't depend on server
 * per-kind stats, which may not populate every metric for every kind). Wall-crossing is free-kick-only.
 */
/**
 * A single kick's on-target contribution in [0,1].
 *  - Binary kinds (Corner/ThrowIn/Goalkeeper) carry a CONTINUOUS success value in `onTarget` (e.g. 0.48).
 *    We use it directly — matching the iPad, whose zone `CombinedSuccessRate` is the average of these
 *    values (so 0% + 48% → 24%, not the 0.75-threshold binary count which would give 0%).
 *  - Discrete kinds (FreeKick/Penalty/Shooting) have no continuous success; contribute 1 when the
 *    result bucket is on-target/goal, else 0 (the classic on-target RATE).
 */
export function onTargetContribution(m: MeasurementMarkerDto): number {
  if (isBinaryOnTargetKind(m.kind) && m.onTarget != null) {
    const v = m.onTarget > 1 ? m.onTarget / 100 : m.onTarget; // tolerate a 0–100 or 0–1 feed
    return Math.max(0, Math.min(1, v));
  }
  return measurementResultBucket(m, m.kind) !== 'offTarget' ? 1 : 0;
}

/** Kind-aware on-target %: average of the continuous success for binary kinds, bucket rate otherwise. */
export function onTargetPercent(measurements: MeasurementMarkerDto[]): number | null {
  if (measurements.length === 0) return null;
  const sum = measurements.reduce((s, m) => s + onTargetContribution(m), 0);
  return (sum / measurements.length) * 100;
}

export function computeKickTileStats(measurements: MeasurementMarkerDto[], kind?: SessionKindGroupName): KickTileStats {
  const totalKicks = measurements.length;
  if (totalKicks === 0) {
    return { totalKicks: 0, onTargetPct: null, avgBallSpeed: null, avgSpinRate: null, avgPullDirection: null, avgSpinAxis: null, avgWallCrossingHeight: null };
  }
  const avg = (get: (m: MeasurementMarkerDto) => number | null | undefined): number | null => {
    const vals = measurements.map(get).filter((v): v is number => v != null);
    return vals.length > 0 ? vals.reduce((sum, v) => sum + v, 0) / vals.length : null;
  };
  return {
    totalKicks,
    onTargetPct: onTargetPercent(measurements),
    avgBallSpeed: avg((m) => m.ballSpeed),
    avgSpinRate: avg((m) => m.spinRate),
    avgPullDirection: avg((m) => m.pullDirection),
    avgSpinAxis: avg((m) => m.spinAxis),
    avgWallCrossingHeight: kind === 'FreeKick' ? avg((m) => m.wallCrossingHeight) : null,
  };
}

// ── Kicks-per-session on-target-rate buckets ──────────────────────────────────

/** A kicks-per-session bucket definition: `[min, max]` range with a resolved label. */
export interface KicksPerSessionBucket {
  label: string;
  min: number;
  max: number;
}

export interface KicksPerSessionBucketData {
  label: string;
  onTargetRate: number;
  count: number;
}

/**
 * Aggregates measurements into per-session kick counts, then buckets sessions by
 * kicks-per-session and computes the on-target rate per bucket, using the shared
 * measurement-aware classifier (goal + on-target both count as on-target).
 * Pure move of the former in-component `buildBucketData` — behaviour unchanged.
 */
export function buildKicksPerSessionBucketData(
  measurements: MeasurementMarkerDto[],
  buckets: KicksPerSessionBucket[],
): KicksPerSessionBucketData[] {
  const bySession = new Map<string, { kicks: number; onTarget: number }>();
  for (const m of measurements) {
    const s = bySession.get(m.sessionId) ?? { kicks: 0, onTarget: 0 };
    s.kicks += 1;
    s.onTarget += onTargetContribution(m); // kind-aware: continuous success for binary kinds
    bySession.set(m.sessionId, s);
  }

  return buckets.map((b) => {
    const sessions = Array.from(bySession.values()).filter((s) => s.kicks >= b.min && s.kicks <= b.max);
    const totalKicks = sessions.reduce((acc, s) => acc + s.kicks, 0);
    const totalOnTarget = sessions.reduce((acc, s) => acc + s.onTarget, 0);
    const onTargetRate = totalKicks > 0 ? (totalOnTarget / totalKicks) * 100 : 0;
    return { label: b.label, onTargetRate, count: sessions.length };
  });
}

// ── Rest-gap on-target-rate buckets ────────────────────────────────────────────

/** A rest-gap bucket definition: inclusive day-gap `[min, max]` range with a resolved label. */
export interface RestGapBucket {
  label: string;
  min: number;
  max: number;
}

export interface RestGapBucketData {
  label: string;
  onTargetRate: number;
  count: number;
  kicks: number;
}

/**
 * Buckets sessions by the day-gap since the previous session and computes the on-target
 * rate per bucket, using the shared measurement-aware classifier (goal + on-target both
 * count as on-target). Session dates come from the sessions store when available, falling
 * back to each measurement's capturedAtUtc. Pure move of the former in-component
 * `buildBucketData` — behaviour unchanged apart from the plotted metric.
 */
export function buildRestGapBucketData(
  measurements: MeasurementMarkerDto[],
  sessions: SessionOptionDto[],
  buckets: RestGapBucket[],
): RestGapBucketData[] {
  const isValidDate = (value: string | null | undefined): value is string => {
    if (!value) return false;
    return !Number.isNaN(new Date(value).getTime());
  };

  // Aggregate kicks + on-target per session
  const sessionMap = new Map<string, { date: string; kicks: number; onTarget: number }>();
  for (const m of measurements) {
    const existing = sessionMap.get(m.sessionId) ?? { date: m.capturedAtUtc, kicks: 0, onTarget: 0 };
    existing.kicks += 1;
    existing.onTarget += onTargetContribution(m); // kind-aware: continuous success for binary kinds
    if (isValidDate(m.capturedAtUtc) && (!isValidDate(existing.date) || new Date(m.capturedAtUtc).getTime() > new Date(existing.date).getTime())) {
      existing.date = m.capturedAtUtc;
    }
    sessionMap.set(m.sessionId, existing);
  }

  // Enrich with session date from sessions store (more accurate than capturedAtUtc)
  for (const s of sessions) {
    if (sessionMap.has(s.id)) {
      const entry = sessionMap.get(s.id)!;
      if (isValidDate(s.capturedAtUtc)) {
        entry.date = s.capturedAtUtc;
      }
      sessionMap.set(s.id, entry);
    }
  }

  // Sort by date
  const sortedSessions = Array.from(sessionMap.entries())
    .filter(([, s]) => isValidDate(s.date))
    .sort(([, a], [, b]) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Compute day gaps
  type SessionWithGap = { date: string; kicks: number; onTarget: number; dayGap: number | null };
  const withGaps: SessionWithGap[] = sortedSessions.map(([, s], i) => {
    if (i === 0) return { ...s, dayGap: null }; // first session has no gap
    const prev = sortedSessions[i - 1][1];
    const msGap = new Date(s.date).getTime() - new Date(prev.date).getTime();
    const dayGap = Math.round(msGap / (1000 * 60 * 60 * 24));
    return { ...s, dayGap };
  });

  return buckets.map((b) => {
    const inBucket = withGaps.filter((s) => s.dayGap !== null && s.dayGap >= b.min && s.dayGap <= b.max);
    const totalKicks = inBucket.reduce((acc, s) => acc + s.kicks, 0);
    const totalOnTarget = inBucket.reduce((acc, s) => acc + s.onTarget, 0);
    const onTargetRate = totalKicks > 0 ? (totalOnTarget / totalKicks) * 100 : 0;
    return { label: b.label, onTargetRate, count: inBucket.length, kicks: totalKicks };
  });
}

// ── Result-breakdown aggregation (groups + trends) ─────────────────────────────

export type ResultBreakdownMetricKey =
  | 'ballSpeed'
  | 'distance'
  | 'hangTime'
  | 'gFactor'
  | 'spinRate'
  | 'launchAngle'
  | 'pullDirection'
  | 'maxHeight'
  | 'wallCrossingHeight';

export type ResultBucketKey = 'goal' | 'onTarget' | 'offTarget';

export interface ResultBreakdownMetricDef {
  key: ResultBreakdownMetricKey;
  labelKey: string;
  unit: string;
  step: number;
  decimals: number;
  customThresholds?: number[];
  /** Use `step` as the exact bucket interval (never grown to fit a bucket cap). */
  hardStep?: boolean;
}

export interface BucketEntry {
  label: string;
  goal: number;
  onTarget: number;
  offTarget: number;
}

const MAX_BUCKETS = 15;

function fmtThreshold(v: number): string {
  return parseFloat(v.toFixed(2)).toString();
}

function collectPairs(
  measurements: MeasurementMarkerDto[],
  metric: ResultBreakdownMetricKey,
): { value: number; result: ResultBucketKey }[] {
  const pairs: { value: number; result: ResultBucketKey }[] = [];
  for (const m of measurements) {
    const v = getMeasurementFieldValue(m, metric);
    if (v !== null && isFinite(v)) pairs.push({ value: v, result: measurementResultBucket(m) });
  }
  return pairs;
}

function trimEmpty(entries: BucketEntry[]): BucketEntry[] {
  let start = 0;
  let end = entries.length - 1;
  while (start < end && entries[start].goal === 0 && entries[start].onTarget === 0 && entries[start].offTarget === 0) start++;
  while (end > start && entries[end].goal === 0 && entries[end].onTarget === 0 && entries[end].offTarget === 0) end--;
  return entries.slice(start, end + 1);
}

function buildUniformBuckets(
  pairs: { value: number; result: ResultBucketKey }[],
  baseStep: number,
  decimals: number,
  unit: string,
  hardStep = false,
): BucketEntry[] {
  const values = pairs.map((p) => p.value);
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const unitSuffix = unit ? ` ${unit}` : '';

  // `hardStep` metrics (distance 1 m, wall crossing 0.2 m, pull 10°) keep their exact interval;
  // others grow the step so a wide range never exceeds the bucket cap.
  let step = baseStep;
  while (!hardStep && step < dataMax - dataMin) {
    const floor = Math.floor(dataMin / step) * step;
    if (Math.floor((dataMax - floor) / step) + 1 <= MAX_BUCKETS) break;
    step *= 2;
  }

  const bucketFloor = Math.floor(dataMin / step) * step;
  const numSteps = Math.floor((dataMax - bucketFloor) / step) + 1;
  const labelDecimals = step < 1 ? decimals : 0;
  const entries: BucketEntry[] = [];

  for (let i = 0; i < numSteps; i++) {
    const lo = parseFloat((bucketFloor + i * step).toFixed(labelDecimals + 2));
    const hi = parseFloat((bucketFloor + (i + 1) * step).toFixed(labelDecimals + 2));
    const fmt = (v: number) => parseFloat(v.toFixed(labelDecimals)).toString();
    const label = `${fmt(lo)}–${fmt(hi)}${unitSuffix}`;
    const entry: BucketEntry = { label, goal: 0, onTarget: 0, offTarget: 0 };
    for (const p of pairs) {
      if (p.value >= lo && p.value < hi) entry[p.result] += 1;
    }
    entries.push(entry);
  }

  return trimEmpty(entries);
}

function buildCustomBuckets(
  pairs: { value: number; result: ResultBucketKey }[],
  thresholds: number[],
  unit: string,
): BucketEntry[] {
  const unitSuffix = unit ? ` ${unit}` : '';
  const entries: BucketEntry[] = [];

  for (let i = 0; i < thresholds.length; i++) {
    const lo = thresholds[i];
    const hi = thresholds[i + 1];
    const label = hi !== undefined
      ? `${fmtThreshold(lo)}–${fmtThreshold(hi)}${unitSuffix}`
      : `≥${fmtThreshold(lo)}${unitSuffix}`;
    const entry: BucketEntry = { label, goal: 0, onTarget: 0, offTarget: 0 };
    for (const p of pairs) {
      const inBucket = hi !== undefined ? p.value >= lo && p.value < hi : p.value >= lo;
      if (inBucket) entry[p.result] += 1;
    }
    entries.push(entry);
  }

  return trimEmpty(entries);
}

/** Buckets measurements by a metric value and counts kick results per bucket. */
export function buildBuckets(measurements: MeasurementMarkerDto[], def: ResultBreakdownMetricDef): BucketEntry[] {
  const pairs = collectPairs(measurements, def.key);
  if (pairs.length === 0) return [];
  if (def.customThresholds) return buildCustomBuckets(pairs, def.customThresholds, def.unit);
  return buildUniformBuckets(pairs, def.step, def.decimals, def.unit, def.hardStep);
}

export interface TrendSession {
  sessionId: string;
  label: string;
  capturedAtUtc: string;
  buckets: Record<ResultBucketKey, { sum: number; count: number }>;
}

function emptyBuckets(): Record<ResultBucketKey, { sum: number; count: number }> {
  return { goal: { sum: 0, count: 0 }, onTarget: { sum: 0, count: 0 }, offTarget: { sum: 0, count: 0 } };
}

/** Groups measurements over time (sessions/weeks/months) and sums metric values per result bucket. */
export function buildTrendData(
  measurements: MeasurementMarkerDto[],
  metric: ResultBreakdownMetricKey,
  groupBy: TrendGroupBy,
): TrendSession[] {
  const groups = new Map<string, TrendSession>();

  for (const m of measurements) {
    const v = getMeasurementFieldValue(m, metric);
    if (v === null || !isFinite(v)) continue;
    const bucket = measurementResultBucket(m);

    let key: string;
    let label: string;
    if (groupBy === 'sessions') {
      key = m.sessionId;
      label = new Date(m.capturedAtUtc).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } else if (groupBy === 'weeks') {
      ({ key, label } = getWeekKey(m.capturedAtUtc));
    } else {
      key = m.capturedAtUtc.slice(0, 7);
      label = new Date(m.capturedAtUtc).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
    }

    let group = groups.get(key);
    if (!group) {
      group = { sessionId: key, label, capturedAtUtc: m.capturedAtUtc, buckets: emptyBuckets() };
      groups.set(key, group);
    }
    if (m.capturedAtUtc > group.capturedAtUtc) group.capturedAtUtc = m.capturedAtUtc;
    group.buckets[bucket].sum += v;
    group.buckets[bucket].count += 1;
  }

  const sorted = Array.from(groups.values()).sort((a, b) => a.capturedAtUtc.localeCompare(b.capturedAtUtc));
  return groupBy === 'sessions' ? sorted.slice(-10) : sorted;
}

// ── Metrics-compare heatmap binning ────────────────────────────────────────────

function niceStep(range: number, bins: number): number {
  const raw = range / bins;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const normalized = raw / magnitude;
  if (normalized < 1.5) return magnitude;
  if (normalized < 3) return 2 * magnitude;
  if (normalized < 7) return 5 * magnitude;
  return 10 * magnitude;
}

function niceBins(min: number, max: number, bins: number, fixedStep?: number): { edges: number[]; labels: string[] } {
  const step = fixedStep ?? niceStep(max - min, bins);
  const start = Math.floor(min / step) * step;
  // A fixed step must span the whole domain (however many bins that takes); otherwise honour `bins`.
  const count = fixedStep ? Math.max(1, Math.ceil((max - start) / step)) : bins;
  const edges: number[] = [];
  for (let v = start; edges.length <= count; v += step) {
    edges.push(Math.round(v * 100) / 100);
  }
  const labels = edges.slice(0, -1).map((e, i) => `${e}–${edges[i + 1]}`);
  return { edges, labels };
}

/** A packed heatmap cell: `[count, xStart, xEnd, yStart, yEnd]`. */
export type HeatmapCell = [number, number, number, number, number];

/**
 * Bins 2D points into a grid of `bins`×`bins` "nice" intervals over the given domain,
 * returning only non-empty cells with their data-coordinate edges.
 * Pure move of the former in-component `heatmapData` binning — behaviour unchanged.
 */
export function buildHeatmapCells(
  points: [number, number][],
  domain: { xMin: number; xMax: number; yMin: number; yMax: number },
  bins: number,
  fixedSteps?: { x?: number; y?: number },
): HeatmapCell[] {
  if (points.length === 0) return [];
  const xBins = niceBins(domain.xMin, domain.xMax, bins, fixedSteps?.x);
  const yBins = niceBins(domain.yMin, domain.yMax, bins, fixedSteps?.y);
  const grid = Array.from({ length: xBins.labels.length }, () =>
    new Array<number>(yBins.labels.length).fill(0)
  );
  for (const [x, y] of points) {
    const xi = xBins.edges.findIndex((e, i) => x >= e && x < xBins.edges[i + 1]);
    const yi = yBins.edges.findIndex((e, i) => y >= e && y < yBins.edges[i + 1]);
    if (xi >= 0 && yi >= 0) grid[xi][yi] += 1;
  }
  const cells: HeatmapCell[] = [];
  for (let xi = 0; xi < xBins.labels.length; xi++) {
    for (let yi = 0; yi < yBins.labels.length; yi++) {
      const count = grid[xi][yi];
      if (count > 0) {
        cells.push([count, xBins.edges[xi], xBins.edges[xi + 1], yBins.edges[yi], yBins.edges[yi + 1]]);
      }
    }
  }
  return cells;
}

export { kickNoun } from 'utilities/kickUtils';

export type MeasurementNumericField =
  | 'ballSpeed' | 'distance' | 'hangTime' | 'gFactor' | 'spinRate'
  | 'spinAxis' | 'launchAngle' | 'launchDirection' | 'pullDirection' | 'maxHeight'
  | 'goalCrossingSpeed' | 'wallCrossingHeight';

export function getMeasurementFieldValue(m: MeasurementMarkerDto, field: MeasurementNumericField): number | null {
  return m[field] ?? null;
}

// ── Free-kick zone aggregation ─────────────────────────────────────────────────

export const ZONE_LOW_SAMPLE_THRESHOLD = 3;

export interface ZoneStat {
  zoneId: number;
  kickCount: number;
  sessionCount: number;
  onTargetPct: number | null;
  goalPct: number | null;
  lowSample: boolean;
}

/** FreeKick zone id of a kick in the given layout (falls back to the layout-1 `zoneId` for older payloads). */
export function getFreeKickZoneIdForLayout(m: MeasurementMarkerDto, layoutId: string): number | undefined {
  const mapped = m.freeKickZoneIds?.[layoutId];
  if (mapped != null) return mapped;
  return layoutId === '1' && m.zoneId != null ? m.zoneId : undefined;
}

/** Keeps a sticky preference only while it is still one of the available options; otherwise `fallback`. */
export function resolveLayoutId(preferred: string | null, availableIds: readonly string[], fallback: string): string {
  return preferred != null && availableIds.includes(preferred) ? preferred : fallback;
}

/** A picked corner side is kept as is; otherwise Left when it has kicks, else Right. */
export function resolveCornerSide(preferred: CornerSide | null, leftKicks: number): CornerSide {
  // A side the user picked is honoured even when it has no kicks (it shows an empty pitch).
  if (preferred) return preferred;
  return leftKicks > 0 ? 'Left' : 'Right';
}

/**
 * Resolve a measurement's corner-zone id for a given layout from its `cornerZones` map
 * (`{ layoutId: zoneId }`). Layout ids are matched exactly, then case-insensitively. Shared so the
 * corner pitch and the tile-scoping logic key kicks to zones identically (never diverging copies).
 */
export function getCornerZoneIdForLayout(
  cornerZones: Record<string, number> | null | undefined,
  layoutId: string,
): number | undefined {
  if (!cornerZones) return undefined;
  const direct = cornerZones[layoutId];
  if (direct != null) return direct;

  const normalizedLayoutId = layoutId.trim().toLowerCase();
  const matchedKey = Object.keys(cornerZones).find((k) => k.trim().toLowerCase() === normalizedLayoutId);
  if (!matchedKey) return undefined;
  return cornerZones[matchedKey];
}

/**
 * Per-zone colour stats derived from zone-stamped markers. On-target/goal classification reuses the
 * shared measurementResultBucket helper (never bespoke math), so every zone view and the summary
 * tile agree by construction. `zoneIdOf` selects each marker's zone id (defaults to `m.zoneId`; the
 * corner pitch passes a layout-specific resolver). Markers whose zone id is null are ignored.
 */
export function buildZoneStats(
  measurements: MeasurementMarkerDto[],
  zoneIdOf: (m: MeasurementMarkerDto) => number | null | undefined = (m) => m.zoneId,
): Map<number, ZoneStat> {
  const acc = new Map<number, { count: number; onTarget: number; goal: number; sessions: Set<string> }>();
  for (const m of measurements) {
    const zoneId = zoneIdOf(m);
    if (zoneId == null) continue;
    const entry = acc.get(zoneId) ?? { count: 0, onTarget: 0, goal: 0, sessions: new Set<string>() };
    entry.count += 1;
    // On-target uses the kind-aware contribution (continuous success for binary kinds, so a corner
    // zone shows the average success — matching the iPad — not a 0.75-threshold binary count).
    entry.onTarget += onTargetContribution(m);
    if (measurementResultBucket(m, m.kind) === 'goal') entry.goal += 1;
    if (m.sessionId) entry.sessions.add(m.sessionId);
    acc.set(zoneId, entry);
  }

  const out = new Map<number, ZoneStat>();
  acc.forEach((e, zoneId) => {
    out.set(zoneId, {
      zoneId,
      kickCount: e.count,
      sessionCount: e.sessions.size,
      onTargetPct: e.count > 0 ? (e.onTarget / e.count) * 100 : null,
      goalPct: e.count > 0 ? (e.goal / e.count) * 100 : null,
      lowSample: e.count < ZONE_LOW_SAMPLE_THRESHOLD,
    });
  });
  return out;
}

export type TimeframePreset = 'week' | 'month' | 'quarter' | 'year' | 'all';

/** Active timeframe: a rolling preset, or a custom range of local `YYYY-MM-DD` days (`to` empty = up to now). */
export interface Timeframe {
  preset: TimeframePreset | 'custom';
  from: string | null;
  to?: string | null;
}

export const DEFAULT_TIMEFRAME: Timeframe = { preset: 'all', from: null, to: null };

const PRESET_MONTHS: Partial<Record<TimeframePreset, number>> = { month: 1, quarter: 3, year: 12 };

function parseIsoDay(day: string | null | undefined): Date | null {
  const match = day ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(day) : null;
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
}

/** Start of the timeframe (inclusive), or null when unbounded. Rolling presets look back from `now`. */
export function timeframeStart(timeframe: Timeframe, now: Date = new Date()): Date | null {
  if (timeframe.preset === 'all') return null;
  if (timeframe.preset === 'custom') return parseIsoDay(timeframe.from);
  const start = new Date(now);
  if (timeframe.preset === 'week') {
    start.setDate(start.getDate() - 7);
  } else {
    start.setMonth(start.getMonth() - (PRESET_MONTHS[timeframe.preset] ?? 0));
  }
  return start;
}

/** End of the timeframe (inclusive, end of the chosen day), or null when open-ended. Only custom ranges have one. */
export function timeframeEnd(timeframe: Timeframe): Date | null {
  const day = timeframe.preset === 'custom' ? parseIsoDay(timeframe.to) : null;
  return day ? new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59, 999) : null;
}

export function isTimeframeActive(timeframe: Timeframe): boolean {
  return timeframeStart(timeframe) !== null;
}

/** Rolling presets that contain at least one of the kicks ('all' always qualifies, so the picker never goes empty). */
export function presetsWithData(measurements: ReadonlyArray<{ capturedAtUtc: string }>, now: Date = new Date()): Set<TimeframePreset> {
  const available = new Set<TimeframePreset>(['all']);
  (['week', 'month', 'quarter', 'year'] as const).forEach((preset) => {
    if (filterByTimeframe(measurements, { preset, from: null }, now).length > 0) available.add(preset);
  });
  return available;
}

/** Keeps only kicks captured within the timeframe; returns the input untouched when unbounded. */
export function filterByTimeframe<T extends { capturedAtUtc: string }>(
  measurements: ReadonlyArray<T>,
  timeframe: Timeframe,
  now: Date = new Date(),
): T[] {
  const start = timeframeStart(timeframe, now);
  if (!start) return measurements as T[]; // untouched (same array) when unbounded
  const startMs = start.getTime();
  const endMs = timeframeEnd(timeframe)?.getTime() ?? Number.POSITIVE_INFINITY;
  return measurements.filter((m) => {
    const capturedMs = new Date(m.capturedAtUtc).getTime();
    return capturedMs >= startMs && capturedMs <= endMs;
  });
}

/** Favourite quick filter: every kick, or only starred ones. */
export type FavoriteFilter = 'all' | 'favorite';

/** Key of a result in the result filter ("Penalty::Goal"): kinds can share result names. */
export const toResultFilterKey = (kind: SessionKindGroupName, result: string): string => `${kind}::${result}`;

/** Which side a corner / throw-in was taken from: left is a positive kickZ, everything else (incl. no data) is right. */
export function cornerSideOf(m: { kickZ?: number | null }): CornerSide {
  return (m.kickZ ?? 0) > 0 ? 'Left' : 'Right';
}

/**
 * The quick filters (result / favourite / side) apply to everything the user sees of the kicks: the table, the goal
 * view and the zone pitch's dots and zone numbers. Zone selection is the one filter the zone pitch itself does not obey
 * (it would blank the other zones), so it is applied separately. The side filter only concerns corners and throw-ins.
 */
export function passesQuickFilters(
  m: MeasurementMarkerDto,
  result: ReadonlyArray<string>,
  favorite: FavoriteFilter,
  sideFilter: ReadonlySet<'left' | 'right'>,
): boolean {
  const byResult = result.length === 0 || result.includes(toResultFilterKey(m.kind, m.result));
  const byFavorite = favorite === 'all' || m.isFavorite === true;
  const sideApplies = sideFilter.size > 0 && (m.kind === 'Corner' || m.kind === 'ThrowIn');
  const bySide = !sideApplies || sideFilter.has(cornerSideOf(m) === 'Left' ? 'left' : 'right');
  return byResult && byFavorite && bySide;
}

/** How many of the player's kicks / throws of this kind the kick's session holds. */
export function sessionKickCount(
  measurements: ReadonlyArray<MeasurementMarkerDto>,
  m: Pick<MeasurementMarkerDto, 'sessionId' | 'playerId' | 'kind'>,
): number {
  return measurements.filter((x) => x.sessionId === m.sessionId && x.playerId === m.playerId && x.kind === m.kind).length;
}
